import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';
import { motion } from 'framer-motion';

const BrochureDownload: React.FC = () => {
  const { projectId, language } = useParams<{ projectId: string; language: string }>();
  const [searchParams] = useSearchParams();
  const [downloadStatus, setDownloadStatus] = useState<'loading' | 'downloading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [downloadProgress, setDownloadProgress] = useState<number>(0);
  
  // Track if download has been initiated to prevent multiple downloads
  const downloadInitiated = useRef<boolean>(false);

  // Get brochure download URL from Convex
  const brochureData = useQuery(
    api.files.getBrochureDownloadUrl,
    projectId ? { projectId: projectId as Id<"projects"> } : "skip"
  );

  const downloadFile = async (url: string, filename: string) => {
    try {
      setDownloadStatus('downloading');
      setDownloadProgress(0);

      // Fetch the file with progress tracking
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      
      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const chunks: Uint8Array[] = [];
      let received = 0;

      // Read the response stream with progress tracking
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        chunks.push(value);
        received += value.length;
        
        if (total > 0) {
          const progress = Math.round((received / total) * 100);
          setDownloadProgress(progress);
        }
      }

      // Create blob from chunks
      const blob = new Blob(chunks, { type: 'application/pdf' });
      
      // Create download link and trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      
      // Append to body, click, and remove (required for Firefox)
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(downloadUrl);
      
      setDownloadStatus('success');
      
    } catch (error) {
      
      setDownloadStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Download failed');
    }
  };

  // Helper function for manual downloads (Download Again button)
  const handleManualDownload = () => {
    if (brochureData) {
      downloadFile(brochureData.url, brochureData.filename);
    }
  };

  useEffect(() => {
    // Prevent multiple downloads
    if (downloadInitiated.current) {
      return;
    }

    if (brochureData === undefined) {
      // Still loading
      return;
    }

    if (brochureData === null) {
      // No brochure found
      setDownloadStatus('error');
      setErrorMessage('Brochure not found for this project.');
      return;
    }

    // Mark download as initiated
    downloadInitiated.current = true;

    // Analytics data tracked internally

    // Start the download process
    downloadFile(brochureData.url, brochureData.filename);

  }, [brochureData]); // Removed searchParams, projectId, language from dependencies

  const getStatusContent = () => {
    switch (downloadStatus) {
      case 'loading':
        return {
          icon: (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"
            />
          ),
          title: 'Preparing Your Brochure...',
          description: 'Please wait while we prepare your download.',
          bgColor: 'bg-blue-50',
          textColor: 'text-blue-700',
        };
      case 'downloading':
        return {
          icon: (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-8 h-8 text-blue-500"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </motion.div>
          ),
          title: 'Downloading Your Brochure...',
          description: `Download progress: ${downloadProgress}%`,
          bgColor: 'bg-blue-50',
          textColor: 'text-blue-700',
        };
      case 'success':
        return {
          icon: (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="w-8 h-8 text-green-500"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </motion.div>
          ),
          title: 'Download Complete!',
          description: 'Thank you for your interest. The brochure has been downloaded to your device.',
          bgColor: 'bg-green-50',
          textColor: 'text-green-700',
        };
      case 'error':
        return {
          icon: (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-8 h-8 text-red-500"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </motion.div>
          ),
          title: 'Download Failed',
          description: errorMessage || 'Sorry, we encountered an issue with your download.',
          bgColor: 'bg-red-50',
          textColor: 'text-red-700',
        };
    }
  };

  const statusContent = getStatusContent();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full"
      >
        <div className={`${statusContent.bgColor} rounded-lg p-8 text-center border border-gray-200 shadow-lg`}>
          <div className="flex justify-center mb-4">
            {statusContent.icon}
          </div>
          
          <h1 className={`text-xl font-semibold ${statusContent.textColor} mb-2`}>
            {statusContent.title}
          </h1>
          
          <p className={`${statusContent.textColor} text-sm mb-6`}>
            {statusContent.description}
          </p>

          {/* Progress bar for downloading state */}
          {downloadStatus === 'downloading' && (
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
              <motion.div
                className="bg-blue-500 h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${downloadProgress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          )}

          {downloadStatus === 'error' && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
            >
              Try Again
            </motion.button>
          )}

          {downloadStatus === 'success' && (
            <div className="space-y-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleManualDownload}
                className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors mr-2"
              >
                Download Again
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.close()}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
              >
                Close Window
              </motion.button>
            </div>
          )}
        </div>

        <div className="text-center mt-4">
          <p className="text-xs text-gray-500">
            {downloadStatus === 'downloading' 
              ? 'Please wait while we download your brochure...'
              : downloadStatus === 'success'
              ? 'Check your Downloads folder for the brochure file.'
              : 'If you experience issues, please contact our support team.'
            }
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default BrochureDownload; 